/**
 * AttendancePage — 2026-06-06 NEW FEATURE
 *
 * Owner/Admin-only attendance + time tracking report. Separate from
 * EOD reports (which capture the qualitative day summary). This page
 * is strictly clock-in / clock-out hours, days worked, totals.
 *
 * Theme rule: theme-aware utilities only (`text-foreground`,
 * `text-muted-foreground`, `text-gold`, `bg-card`, `bg-muted/30`,
 * `border-border`). No hardcoded hex anywhere.
 *
 * Access: client-side guard hides the page from non-admin/owner. The
 * backend also gates the endpoint with `require_admin_or_owner`, so
 * even if someone bypasses the UI the API rejects them.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { attendanceAPI, workspaceAPI, clockAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Loader2, Clock, Calendar, Users, TrendingUp, ChevronDown, ChevronRight, Coffee, Pencil, LogOut, Coffee as CoffeeOff } from 'lucide-react';
import { toast } from 'sonner';
import { formatDuration, formatBreakSegment, normalizeBreaks } from '../lib/breakUtils';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'team', label: 'Team' },
];

function fmtTime(iso, tz) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: '2-digit', minute: '2-digit', ...(tz ? { timeZone: tz } : {}),
    });
  } catch {
    return '—';
  }
}

function tzAbbrev(tz) {
  if (!tz) return '';
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

function fmtHours(h) {
  if (!h || h === 0) return '0h';
  const whole = Math.floor(h);
  const m = Math.round((h - whole) * 60);
  return m > 0 ? `${whole}h ${m}m` : `${whole}h`;
}

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function AttendancePage() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const myRole = user?.role || '';
  const wsRole = currentWorkspace?.role || '';
  // 2026-02-20 — Restricted access: Attendance & Time Tracking is now
  // OWNER-only. Admins (workspace + global) are no longer permitted.
  // Super-admin (Dhrumil's global role) stays exempt for cross-workspace
  // support visibility. The backend endpoint enforces the same gate.
  const canView = myRole === 'super_admin' || wsRole === 'owner';

  const [startDate, setStartDate] = useState(isoDaysAgo(7));
  const [endDate, setEndDate] = useState(todayIso());
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('');
  const [report, setReport] = useState({ period: null, members: [] });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  // 2026-02 — Break Insights: track which day rows are expanded to show the
  // browser-local break-segment breakdown. Key = `${user_id}:${date}`.
  const [expandedRows, setExpandedRows] = useState({});
  // 2026-02 — Owner/Admin manual correction dialog state.
  // `{member, day}` when open, null when closed.
  const [editRow, setEditRow] = useState(null);

  // Pull assignable members (un-scoped roster) so the Team filter is
  // populated regardless of caller hierarchy.
  useEffect(() => {
    if (!currentWorkspace?.id || !canView) return;
    workspaceAPI
      .listAssignableMembers(currentWorkspace.id)
      .then((r) => setMembers((r.data || []).map((m) => ({ ...m.user, role: m.role })).filter(Boolean)))
      .catch(() => setMembers([]));
  }, [currentWorkspace?.id, canView]);

  const fetchReport = useCallback(async () => {
    if (!currentWorkspace?.id || !canView) return;
    setLoading(true);
    try {
      const r = await attendanceAPI.report(currentWorkspace.id, {
        start_date: startDate,
        end_date: endDate,
        user_id: userId || undefined,
        role: role || undefined,
      });
      setReport(r.data || { period: null, members: [] });
    } catch (e) {
      toast.error('Could not load attendance report');
      setReport({ period: null, members: [] });
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id, canView, startDate, endDate, userId, role]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const totals = useMemo(() => {
    const ms = report?.members || [];
    return {
      members: ms.length,
      members_with_hours: ms.filter((m) => (m.total_hours || 0) > 0).length,
      total_hours: ms.reduce((sum, m) => sum + (m.total_hours || 0), 0),
      total_days_worked: ms.reduce((sum, m) => sum + (m.days_worked || 0), 0),
    };
  }, [report]);

  if (!canView) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="attendance-no-access">
        <Card>
          <CardHeader>
            <CardTitle>Access restricted</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              The Attendance &amp; Time Tracking report is restricted to the workspace owner. If you need this view, ask the workspace owner.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="attendance-page">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Clock className="h-6 w-6 text-gold" /> Attendance &amp; Time Tracking
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Clock-in / clock-out summary for your team. Owner &amp; Admin only.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchReport}
          disabled={loading}
          data-testid="attendance-refresh"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label htmlFor="att-start" className="text-xs">From</Label>
            <Input
              id="att-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="attendance-start-date"
            />
          </div>
          <div>
            <Label htmlFor="att-end" className="text-xs">To</Label>
            <Input
              id="att-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="attendance-end-date"
            />
          </div>
          <div>
            <Label htmlFor="att-user" className="text-xs">Team member</Label>
            <select
              id="att-user"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              data-testid="attendance-user-filter"
              className="w-full h-10 rounded-md bg-background border border-border px-3 text-sm"
            >
              <option value="">All members</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name || m.email}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="att-role" className="text-xs">Role</Label>
            <select
              id="att-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="attendance-role-filter"
              className="w-full h-10 rounded-md bg-background border border-border px-3 text-sm"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setStartDate(isoDaysAgo(7)); setEndDate(todayIso()); }}
              data-testid="attendance-preset-week"
              className="flex-1"
            >This week</Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setStartDate(isoDaysAgo(30)); setEndDate(todayIso()); }}
              data-testid="attendance-preset-month"
              className="flex-1"
            >Month</Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Users className="h-3.5 w-3.5" /> Members in scope
            </div>
            <div className="text-2xl font-semibold mt-1 text-foreground">{totals.members}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{totals.members_with_hours} clocked in</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> Total hours
            </div>
            <div className="text-2xl font-semibold mt-1 text-gold">{fmtHours(totals.total_hours)}</div>
            <div className="text-xs text-muted-foreground mt-0.5">across {report?.period?.days ?? '—'} days</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" /> Days worked
            </div>
            <div className="text-2xl font-semibold mt-1 text-foreground">{totals.total_days_worked}</div>
            <div className="text-xs text-muted-foreground mt-0.5">team total in period</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <TrendingUp className="h-3.5 w-3.5" /> Avg per active member
            </div>
            <div className="text-2xl font-semibold mt-1 text-foreground">
              {totals.members_with_hours > 0
                ? fmtHours(totals.total_hours / totals.members_with_hours)
                : '—'}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">over the selected period</div>
          </CardContent>
        </Card>
      </div>

      {/* Per-member tables */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (report?.members || []).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground text-sm">
            No members match the current filters.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {(report.members || []).map((m) => (
            <Card key={m.user_id} data-testid={`attendance-member-${m.user_id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="text-xs bg-muted text-foreground">
                        {(m.name || m.email || '?').slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium text-foreground text-[15px]">{m.name || m.email}</div>
                      <div className="text-xs text-muted-foreground">{m.email}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] uppercase">{m.role}</Badge>
                  </div>
                  <div className="flex items-center gap-5 text-sm">
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Total</div>
                      <div className="text-gold font-semibold" data-testid={`attendance-total-${m.user_id}`}>
                        {fmtHours(m.total_hours)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Break</div>
                      <div className="text-amber-300 font-semibold" data-testid={`attendance-total-break-${m.user_id}`}>
                        {formatDuration(m.total_break_minutes || 0)}
                      </div>
                    </div>
                    {/* 2026-02-20 — Wellness signal: avg length per break. */}
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Avg break</div>
                      <div
                        className="font-semibold text-foreground"
                        data-testid={`attendance-avg-break-${m.user_id}`}
                        title={`${m.total_break_count || 0} break${m.total_break_count === 1 ? '' : 's'} taken in this period`}
                      >
                        {(m.avg_break_minutes_per_session || 0) > 0
                          ? `${m.avg_break_minutes_per_session} min`
                          : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-muted-foreground">Days</div>
                      <div className="font-semibold text-foreground">{m.days_worked}</div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {(m.daily || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">No clock-ins recorded.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="text-left text-[10px] uppercase text-muted-foreground border-b border-border">
                          <th className="py-2 pr-3 font-medium w-6"></th>
                          <th className="py-2 pr-3 font-medium">Date</th>
                          <th className="py-2 pr-3 font-medium">Clock in</th>
                          <th className="py-2 pr-3 font-medium">Clock out</th>
                          <th className="py-2 pr-3 font-medium">Sessions</th>
                          <th className="py-2 pr-3 font-medium text-right">Break</th>
                          <th className="py-2 pr-3 font-medium text-right">Hours</th>
                          <th className="py-2 pr-1 font-medium text-right w-8" aria-label="Edit"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(m.daily || []).map((d) => {
                          const dayBreaks = normalizeBreaks(d.breaks || []);
                          const hasBreaks = dayBreaks.length > 0;
                          const rowKey = `${m.user_id}:${d.date}`;
                          const isOpen = !!expandedRows[rowKey];
                          return (
                            <React.Fragment key={d.date}>
                              <tr
                                className={`border-b border-border/30 last:border-0 ${hasBreaks ? 'cursor-pointer hover:bg-foreground/[0.02]' : ''}`}
                                onClick={() => hasBreaks && setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))}
                                data-testid={`attendance-row-${m.user_id}-${d.date}`}
                              >
                                <td className="py-2 pr-1">
                                  {hasBreaks ? (
                                    <span className="inline-flex items-center text-muted-foreground/60" data-testid={`attendance-expand-${m.user_id}-${d.date}`}>
                                      {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                    </span>
                                  ) : null}
                                </td>
                                <td className="py-2 pr-3 text-foreground whitespace-nowrap">
                                  {d.date}
                                  {d.tz && tzAbbrev(d.tz) && (
                                    <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-foreground/[0.06] text-muted-foreground"
                                          title={`${d.tz} — this member's local day`}
                                          data-testid={`attendance-tz-chip-${m.user_id}-${d.date}`}>
                                      {tzAbbrev(d.tz)}
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 pr-3 text-muted-foreground">{fmtTime(d.clock_in, d.tz)}</td>
                                <td className="py-2 pr-3 text-muted-foreground">{fmtTime(d.clock_out, d.tz)}</td>
                                <td className="py-2 pr-3 text-muted-foreground">{d.sessions}</td>
                                <td className="py-2 pr-3 text-right text-amber-300 font-medium" data-testid={`attendance-break-${m.user_id}-${d.date}`}>
                                  {hasBreaks ? formatDuration(d.break_minutes || 0) : '—'}
                                </td>
                                <td className="py-2 pr-3 text-right text-foreground font-medium">{fmtHours(d.hours)}</td>
                                <td className="py-2 pr-1 text-right">
                                  {/* 2026-02-23 — Admin override: end-break + clock-out
                                      on behalf of a stuck team member. Shown ONLY for
                                      today's row when state shows the member is still
                                      open (no clock_out yet) and/or stuck on break. */}
                                  {d.date === todayIso() && !d.clock_out && (
                                    <AdminOverrideButtons
                                      workspaceId={currentWorkspace?.id}
                                      memberId={m.user_id}
                                      memberName={m.name}
                                      onBreak={(dayBreaks.length > 0) && !dayBreaks[dayBreaks.length - 1]?.end}
                                      onChanged={fetchReport}
                                    />
                                  )}
                                  {/* 2026-02 — Owner/Admin manual correction
                                      for a missed clock-out (or backfill).
                                      Clicking opens the edit dialog with
                                      this row's current values pre-filled. */}
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setEditRow({ member: m, day: d }); }}
                                    className="inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground/50 hover:text-gold hover:bg-foreground/[0.04] ml-1"
                                    data-testid={`attendance-edit-${m.user_id}-${d.date}`}
                                    title="Correct clock-in / clock-out"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </td>
                              </tr>
                              {hasBreaks && isOpen && (
                                <tr className="border-b border-border/20 bg-foreground/[0.015]">
                                  <td></td>
                                  <td colSpan={7} className="py-2 pr-3" data-testid={`attendance-break-detail-${m.user_id}-${d.date}`}>
                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground/60 mb-1">
                                      <Coffee className="h-3 w-3" /> Break segments (browser-local time)
                                    </div>
                                    <ul className="space-y-0.5">
                                      {dayBreaks.map((b, i) => (
                                        <li key={i} className="text-[11px] text-muted-foreground/80 font-mono">
                                          {formatBreakSegment(b, i)}
                                        </li>
                                      ))}
                                    </ul>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* 2026-02 — Owner/Admin manual attendance correction dialog. */}
      <CorrectionDialog
        row={editRow}
        onClose={() => setEditRow(null)}
        onSaved={() => { setEditRow(null); fetchReport(); }}
        workspaceId={currentWorkspace?.id}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 2026-02 — Manual attendance correction dialog. Owner/Admin only — the
// `Pencil` icon is rendered on every day row; clicking it opens this
// dialog pre-filled with the day's current clock-in / clock-out times
// in BROWSER-LOCAL form (the user-visible representation). On save we
// convert back to ISO-UTC before hitting the backend.
// ──────────────────────────────────────────────────────────────────────
function isoToLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}
function localInputToIso(s) {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch { return null; }
}

function CorrectionDialog({ row, onClose, onSaved, workspaceId }) {
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!row) return;
    setClockIn(isoToLocalInput(row.day?.clock_in));
    setClockOut(isoToLocalInput(row.day?.clock_out));
    setNote('');
  }, [row]);
  if (!row) return null;
  const { member, day } = row;
  const save = async () => {
    if (!workspaceId) return;
    const ci = localInputToIso(clockIn);
    const co = localInputToIso(clockOut);
    if (!ci && !co) {
      toast.error('Provide a clock-in or clock-out time to update.');
      return;
    }
    setSaving(true);
    try {
      await attendanceAPI.correct({
        workspace_id: workspaceId,
        user_id: member.user_id,
        date: day.date,
        clock_in_time: ci || undefined,
        clock_out_time: co || undefined,
        note: note.trim() || undefined,
      });
      toast.success('Attendance corrected. Hours recalculated.');
      onSaved && onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save correction');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose && onClose(); }}>
      <DialogContent className="sm:max-w-[460px] bg-card border-border/40" data-testid="attendance-correction-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[17px]">Correct attendance</DialogTitle>
          <DialogDescription className="text-[12px] text-muted-foreground/60">
            {member.name || member.email} — {day.date}. Times are shown in your browser-local timezone and saved as UTC.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="cd-clock-in" className="text-[12px]">Clock in</Label>
            <Input
              id="cd-clock-in"
              type="datetime-local"
              value={clockIn}
              onChange={(e) => setClockIn(e.target.value)}
              data-testid="attendance-correction-clock-in"
              className="h-10 text-[13px] bg-foreground/[0.02] border-border/30"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cd-clock-out" className="text-[12px]">Clock out</Label>
            <Input
              id="cd-clock-out"
              type="datetime-local"
              value={clockOut}
              onChange={(e) => setClockOut(e.target.value)}
              data-testid="attendance-correction-clock-out"
              className="h-10 text-[13px] bg-foreground/[0.02] border-border/30"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cd-note" className="text-[12px]">Note (optional)</Label>
            <Textarea
              id="cd-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Forgot to clock out — corrected by Owner"
              rows={2}
              data-testid="attendance-correction-note"
              className="text-[13px] bg-foreground/[0.02] border-border/30"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="attendance-correction-save"
            className="bg-gold/10 hover:bg-gold/20 text-gold border-0 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save correction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────
// 2026-02-23 — Admin override: end stuck break OR clock-out a team
// member who can't do it themselves (device error, app frozen, etc.).
// Renders only for today's row when the member is still open.
// ──────────────────────────────────────────────────────────────────
function AdminOverrideButtons({ workspaceId, memberId, memberName, onBreak, onChanged }) {
  const [busy, setBusy] = useState(false);
  const handleEndBreak = async (e) => {
    e.stopPropagation();
    if (!workspaceId || !memberId) return;
    setBusy(true);
    try {
      await clockAPI.adminEndBreak(workspaceId, memberId);
      toast.success(`Ended break for ${memberName || 'team member'}`);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to end break');
    } finally {
      setBusy(false);
    }
  };
  const handleClockOut = async (e) => {
    e.stopPropagation();
    if (!workspaceId || !memberId) return;
    if (!window.confirm(`Clock out ${memberName || 'this team member'}?\n\nAny open break will be ended first. This action is recorded as an admin override.`)) return;
    setBusy(true);
    try {
      await clockAPI.adminClockOut(workspaceId, memberId, 'Stuck — admin override');
      toast.success(`Clocked out ${memberName || 'team member'}`);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clock out');
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1">
      {onBreak && (
        <button
          type="button"
          onClick={handleEndBreak}
          disabled={busy}
          className="inline-flex items-center h-6 px-1.5 rounded text-[10px] border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-40"
          data-testid={`admin-end-break-${memberId}`}
          title={`End break for ${memberName}`}
        >
          <CoffeeOff className="h-3 w-3 mr-1" /> End break
        </button>
      )}
      <button
        type="button"
        onClick={handleClockOut}
        disabled={busy}
        className="inline-flex items-center h-6 px-1.5 rounded text-[10px] border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40"
        data-testid={`admin-clock-out-${memberId}`}
        title={`Clock out ${memberName}`}
      >
        <LogOut className="h-3 w-3 mr-1" /> Clock out
      </button>
    </span>
  );
}


